<template>
    <div class="message-traffic-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>WABA Message Traffic</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: 'Chart View' }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: 'Table View' }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Download' }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: 'Fullscreen' }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <AnimatedLineChart
            :title="$t('report.messageTraffic')"
            :x-axis-data="getXAxisData()"
            :y-axis-data="getYAxisData()"
            :series="getSeries()"
            v-if="!tableMode"
            :override-options="overrideOptions()"
            :key="getChartKey()"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats"></div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";
import AnimatedLineChart from "../../Pages/Chatbot/pages/reports/charts/AnimatedLineChart.vue";
import StringUtils from "../../../utils/StringUtils";

const log = log2("MessageTraffic");
export default Vue.extend({
    name: "MessageTraffic",
    components: { AnimatedLineChart, ChartDoddleIcon, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FF0087", "#FFBF00"],
            legends: [this.$t("report.visitorCount")],
            stats: {
                TotalVisitorCount: 0,
                AverageVisitorCount: 0,
                MaxVisitorCount: 0,
                MinVisitorCount: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
                broadcasts: [],
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        overrideOptions() {
            return {
                color: ["#37A2FF", "#FF0087"],
                legend: {
                    data: ["Incoming Messages", "Outgoing Messages"],
                },
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".message-traffic-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getChartKey() {
            return StringUtils.hashCodeObj({ data: this.graphData, filter: this.filters });
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.wabaMessageTraffic(this.filters)
                .then((res) => {
                    log("wabaMessageTraffic data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                    // scopedSlots: { customRender: "date" },
                },
                {
                    title: this.$t("report.incomingMessage"),
                    dataIndex: "incoming",
                    key: "incoming",
                },
                {
                    title: this.$t("report.outgoingMessage"),
                    dataIndex: "outgoing",
                    key: "outgoing",
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getRows.buckets", buckets);
            const rows = buckets.map((bucket) => {
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    incoming: bucket.incoming,
                    outgoing: bucket.outgoing,
                };
            });
            return rows;
        },
        getStats() {
            return {};
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            const data = buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
            return {
                type: "category",
                data,
            };
        },
        getYAxisData() {
            return {
                name: "Message Count",
                type: "value",
            };
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getSeries.buckets", buckets);
            const incomingMessageData = buckets.map((bucket) => ({ value: bucket.incoming }));
            const outgoingMessageData = buckets.map((bucket) => ({ value: bucket.outgoing }));
            let broadcasts = this.graphData.broadcasts.map((broadcast) => {
                let annotationDate = moment(broadcast.date).format("DD/MM/YYYY HH:mm");
                if (timeSeriesType === "day") {
                    annotationDate = moment(broadcast.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    annotationDate = moment(broadcast.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    annotationDate = moment(broadcast.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    annotationDate = moment(broadcast.date).format("MMM");
                }
                broadcast.annotationDate = annotationDate;
                return broadcast;
            });
            const grouppedBroadcasts = _.groupBy(broadcasts, "annotationDate");
            broadcasts = Object.keys(grouppedBroadcasts).map((key) => {
                return {
                    title: `${grouppedBroadcasts[key].length} T.Mesaj`,
                    annotationDate: key,
                };
            });
            const seriesObject = (name, data) => ({
                name,
                type: "line",
                showSymbol: false,
                // smooth: true,
                endLabel: {
                    show: true,
                    formatter: function (params) {
                        return name + ": " + params.value;
                    },
                },
                labelLayout: {
                    moveOverlap: "shiftY",
                },
                emphasis: {
                    focus: "series",
                },
                markLine: {
                    data: broadcasts.map((broadcast) => {
                        return {
                            name: "broadcast",
                            xAxis: broadcast.annotationDate,
                            label: {
                                show: true,
                                formatter: function (params) {
                                    return broadcast.title;
                                },
                                color: "#8400ff",
                            },
                            lineStyle: {
                                color: "red",
                            },
                        };
                    }),
                },
                data,
            });

            const series = [
                seriesObject("Incoming Messages", incomingMessageData),
                seriesObject("Outgoing Messages", outgoingMessageData),
            ];
            log("getSeries.series", series);

            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            log("exportToExcel.buckets", buckets);
            const data = buckets.map((bucket) => {
                const newVisitorCount = bucket.newVisitorCount;
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    newVisitorCount,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam Ziyaretci Sayısı",
                    newVisitorCount: stats.TotalVisitorCount,
                },
                {
                    date: "Ortalama Ziyaretci Sayısı",
                    newVisitorCount: stats.AverageVisitorCount,
                },
                {
                    date: "Maksimum Ziyaretci Sayısı",
                    newVisitorCount: stats.MaxVisitorCount,
                },
                {
                    date: "Minimum Ziyaretci Sayısı",
                    newVisitorCount: stats.MinVisitorCount,
                },
            ];
            expertToExcel(excelData, `website-traffic.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".message-traffic-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.message-traffic-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.message-traffic-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
